import React from 'react';
import { useSelector } from 'react-redux';
import Drawer from './Drawer';
import { translate } from '../Services/translation';
import MiniCartProduct from './MiniCartProduct';

type MinicartProps = {
  includeVat: boolean;
  quantity: number;
  orderRows: any[];
  orderTotal: number;
  checkoutUrl: string;
  showInfo: boolean;
  toggle: () => void;
  update: () => void;
  datalayerViewCart: (ecommerce: any) => void;
};

const MiniCartDrawer: React.FC<MinicartProps> = ({
  includeVat,
  quantity,
  orderRows,
  orderTotal,
  checkoutUrl,
  showInfo,
  toggle,
  update,
  datalayerViewCart,
}) => {
  const cart = useSelector((state: any) => state.cart);
  const { gtmViewModel } = cart || {};
  const { ecommerce } = gtmViewModel || {};

  const onCartClick = () => {
    toggle();
    if (orderRows?.length > 0 && datalayerViewCart && ecommerce) {
      datalayerViewCart(ecommerce);
    }
  };

  const cartHeader = (
    <div className="cart__header py-[23px] w-full flex items-center justify-between">
      <h2 className="uppercase">{translate('minicart.title')}</h2>
      <button
        className="rounded-[40px] h-[30px] py-[18px] px-3 flex items-center justify-center"
        onClick={() => toggle()}
      >
        {translate('minicart.close')}
      </button>
    </div>
  );
  return (
    <>
      <button
        className="flex flex-col items-center justify-center"
        onClick={onCartClick}
      >
        <i className="cart--icon">
          <span className="cart__quantity">{quantity}</span>
        </i>
        <span aria-live="polite" aria-atomic="true" className="visually-hidden">
          {translate('cart.quantity.update') + ' ' + quantity}
        </span>
        <span className="caption-1 mt-[6px] hidden lg:block">
          {translate('minicart.checkout')}
        </span>
      </button>
      <Drawer
        isOpen={showInfo}
        onClose={toggle}
        closeButtonAriaLabel={translate('minicart.close')}
        headerContent={cartHeader}
        hideCloseButton={true}
        wideDrawer={true}
        className="cart__info"
        headerClassName="!pl-5"
      >
        <div className="cart__info-container">
          <div className="mini-cart-wrap__scroll">
            {orderRows.map((orderRow) => {
              const { rowSystemId, quantity, size, packageQuantity } = orderRow;
              return (
                <MiniCartProduct
                  key={rowSystemId}
                  product={orderRow}
                  articleNumber={orderRow.articleNumber}
                  includeVat={includeVat}
                  onCartItemIncrement={() =>
                    update(orderRow.articleNumber, quantity + packageQuantity)
                  }
                  onCartItemDecrement={() =>
                    update(orderRow.articleNumber, quantity - packageQuantity)
                  }
                  onCartItemRemove={() => update(orderRow.articleNumber, 0)}
                />
              );
            })}
          </div>
          <div className="cart__summary-section">
            <div className="cart__summary-section--total-amount">
              {orderRows.length > 0 ? (
                <>
                  <div
                    aria-live="polite"
                    aria-atomic="true"
                    className="visually-hidden"
                  >
                    {translate('orderdetail.grandtotal.update') +
                      ' ' +
                      orderTotal}
                  </div>
                  <b>{translate('minicart.total')}</b>
                  <div className="cart__summary-section--price-vat">
                    <span>{orderTotal}</span>
                    <span>
                      {includeVat
                        ? translate('minicart.inclvat')
                        : translate('minicart.exclvat')}
                    </span>
                  </div>
                </>
              ) : (
                <span>{translate('checkout.cart.empty')}</span>
              )}
            </div>
            {orderRows.length > 0 ? (
              <a href={checkoutUrl} className="cart__checkout-button">
                {translate('minicart.checkout')}
              </a>
            ) : null}
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default MiniCartDrawer;
